import { ApiResponse, PaginatedApiResponse } from '@/types/api.types';
import baseApi from './RootBaseApi';
import {
  CreateServiceProviderPayload,
  UpdateServiceProviderPayload,
  ChangeServiceProviderStatusPayload,
  ServiceProviderResponse,
  ServiceProviderListQueryParams,
  ServiceProviderDetailResponse,
  Service,
} from '@/types/service-provider.types';
import { HarborKYCData } from '@/types/harbor-management.types';
import { KYCType } from '@/constant';

export const ServiceProviderService = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getServiceProviders: builder.query<
      PaginatedApiResponse<ServiceProviderResponse[]>,
      ServiceProviderListQueryParams
    >({
      query: (params) => ({
        url: 'service-providers',
        method: 'GET',
        params: {
          page: params.page,
          limit: params.limit,
          status: params.status,
          search: params.search,
          country: params.country,
          sort_by: params.sort_by,
          sort_order: params.sort_order,
          exclude_pending: params.exclude_pending,
        },
      }),
      providesTags: ['ServiceProviders'],
      keepUnusedDataFor: 0,
    }),

    getPendingServiceProviders: builder.query<
      PaginatedApiResponse<ServiceProviderResponse[]>,
      ServiceProviderListQueryParams
    >({
      query: (params) => ({
        url: 'service-providers/pending',
        method: 'GET',
        params: {
          page: params.page,
          limit: params.limit,
          search: params.search,
          sort_by: params.sort_by,
          sort_order: params.sort_order,
        },
      }),
      providesTags: ['ServiceProviders'],
      keepUnusedDataFor: 0,
    }),

    approveServiceProvider: builder.mutation<
      ApiResponse<ServiceProviderResponse>,
      { id: string; data: ChangeServiceProviderStatusPayload }
    >({
      query: ({ id, data }) => ({
        url: `/service-providers/${id}/approve`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['ServiceProviders'],
    }),

    rejectServiceProvider: builder.mutation<
      ApiResponse<ServiceProviderResponse>,
      { id: string; data: ChangeServiceProviderStatusPayload }
    >({
      query: ({ id, data }) => ({
        url: `/service-providers/${id}/reject`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['ServiceProviders'],
    }),

    createServiceProvider: builder.mutation<
      ApiResponse<ServiceProviderResponse>,
      CreateServiceProviderPayload
    >({
      query: (data) => ({
        url: 'service-providers',
        method: 'POST',
        data,
      }),
      invalidatesTags: ['ServiceProviders'],
    }),

    getServiceProviderById: builder.query<
      ServiceProviderDetailResponse,
      string
    >({
      query: (id) => ({
        url: `/service-providers/${id}`,
        method: 'GET',
      }),
      providesTags: (result, error, id) => [{ type: 'ServiceProviders', id }],
      keepUnusedDataFor: 0,
    }),

    updateServiceProvider: builder.mutation<
      ApiResponse<ServiceProviderResponse>,
      { id: string; data: UpdateServiceProviderPayload }
    >({
      query: ({ id, data }) => ({
        url: `/service-providers/${id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: ['ServiceProviders'],
    }),

    changeServiceProviderStatus: builder.mutation<
      ApiResponse<ServiceProviderResponse>,
      { id: string; data: ChangeServiceProviderStatusPayload }
    >({
      query: ({ id, data }) => ({
        url: `/service-providers/${id}/status`,
        method: 'PATCH',
        data,
      }),
      invalidatesTags: ['ServiceProviders'],
    }),

    deleteServiceProvider: builder.mutation<ApiResponse<null>, string>({
      query: (id) => ({
        url: `/service-providers/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ServiceProviders'],
    }),

    getActiveServices: builder.query<ApiResponse<Service[]>, void>({
      query: () => ({
        url: 'service-providers/services/active',
        method: 'GET',
      }),
      providesTags: ['ServiceProviders'],
      keepUnusedDataFor: 300, // Cache for 5 minutes
    }),

    // Get available harbors for linking
    getAvailableHarbors: builder.query<
      ApiResponse<{ id: string; name: string }[]>,
      { showharborlisting?: boolean }
    >({
      query: (params) => ({
        url: 'harbors',
        method: 'GET',
        params: {
          showharborlisting: params?.showharborlisting,
        },
      }),
      providesTags: ['Harbors'],
      keepUnusedDataFor: 300, // Cache for 5 minutes
    }),

    getServiceProviderKYC: builder.query<ApiResponse<HarborKYCData>, string>({
      query: (entityId) => ({
        url: `kyc/entity/${KYCType.SERVICE_PROVIDER}/${entityId}`,
        method: 'GET',
      }),
      transformResponse: (response: ApiResponse<HarborKYCData>) => response,
      providesTags: (result, error, entityId) => [
        { type: 'ServiceProviders', id: `${entityId}-kyc` },
      ],
      keepUnusedDataFor: 0,
    }),

    updateKYCStatus: builder.mutation<
      ApiResponse<HarborKYCData>,
      {
        kycId: string;
        entityId: string;
        data: { kyc_status: string; admin_notes: string };
      }
    >({
      query: ({ kycId, data }) => ({
        url: `admin/kyc/${kycId}/status`,
        method: 'PATCH',
        data,
      }),
      transformResponse: (response: ApiResponse<HarborKYCData>) => response,
      invalidatesTags: (result, error, { entityId }) => [
        { type: 'ServiceProviders', id: `${entityId}-kyc` },
      ],
    }),
  }),
});

export const {
  useGetServiceProvidersQuery,
  useGetPendingServiceProvidersQuery,
  useCreateServiceProviderMutation,
  useGetServiceProviderByIdQuery,
  useUpdateServiceProviderMutation,
  useChangeServiceProviderStatusMutation,
  useDeleteServiceProviderMutation,
  useGetActiveServicesQuery,
  useGetAvailableHarborsQuery,
  useApproveServiceProviderMutation,
  useRejectServiceProviderMutation,
  useGetServiceProviderKYCQuery,
  useUpdateKYCStatusMutation,
} = ServiceProviderService;
